import React, { useEffect, useState } from 'react';
import Loader from "../../components/Loader"; 
import Paginator from "../../components/Paginator"; 
import ApiService from "../../services/ApiService";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Link, useSearchParams  } from 'react-router-dom';
import { Modal, Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { getGenericStatus } from '../../utils/statusHelper';
import { formatDate } from '../../utils/helper';

const List = () => {
    
    const [isLoader, setIsLoader] = useState(false);
    const [users, setUsers] = useState([]);
    const [pagination, setPagination] = useState({});
  
    const [searchParams] = useSearchParams();
    const role = searchParams.get('role'); // Get role from URL
    const status = searchParams.get('status');

    const [apiData, setApiData] = useState({
        search: "",
        page: 1,
    });

    // Change Password Modal State
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordData, setPasswordData] = useState({ user_id: null, password: '', password_confirmation: '' });

    useEffect(() => {
        getUsers();
    }, [apiData.page, apiData.search, role, status]); 

    const openPasswordModal = (user) => {
        setPasswordData({ user_id: user.id, password: '', password_confirmation: '' });
        setShowPasswordModal(true);
    };

    const submitPasswordChange = async () => {
        if (!passwordData.password || passwordData.password !== passwordData.password_confirmation) {
            toast.error("Passwords do not match or are empty");
            return;
        }
        
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: 'POST',
                url: `admin/users/${passwordData.user_id}/change-password`,
                data: {
                    password: passwordData.password,
                    password_confirmation: passwordData.password_confirmation
                }
            });
            
            if (response.data.status) {
                toast.success(response.data.message);
                setShowPasswordModal(false);
            } else {
                toast.error(response.data.message);
            }
            setIsLoader(false);
        } catch (error) {
            setIsLoader(false);
            toast.error(error.response?.data?.message || 'Something went wrong');
        }
    };

    const getUsers = async () => {
        try {
            setIsLoader(true);
            const requestData = {
                ...apiData,
                role: role, // Pass role to API
                status: status
            };
            const response = await ApiService.request({
                method: "GET",
                url: "admin/users", 
                data: requestData
            });
            const data = response.data;
            if (data.status) {
                setUsers(data.data.users || []); // Assuming API returns users key
                setPagination(data.data.pagination || {});
            }
        } catch (error) {
            console.error(error);
            // toast.error("Failed to fetch users");
        } finally {
            setIsLoader(false);
        }
    };
    
    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await ApiService.request({
                        method: "DELETE",
                        url: `admin/users/${id}`
                    });
                    if (response.data.status) {
                        toast.success(response.data.message);
                        getUsers();
                    } else {
                        toast.error(response.data.message);
                    }
                } catch (error) {
                    toast.error(error.response?.data?.message || "Failed to delete user");
                }
            }
        });
    };

    const getTitle = () => {
        if (role === 'admin') return 'Admins';
        if (role === 'doctor') return 'Doctors';
        if (role === 'receptionist') return 'Receptionists';
        if (role === 'specialist') return 'Specialists';
        return 'Users';
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            {getTitle()} List
                        </h1>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <OverlayTrigger placement="top" overlay={<Tooltip>Add {getTitle()}</Tooltip>}>
                            <Link to={`/users/create?role=${role}`} className="btn btn-sm fw-bold btn-base">
                                Add {getTitle()}
                            </Link>
                        </OverlayTrigger>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <div className="d-flex align-items-center position-relative my-1">
                                    <span className="svg-icon svg-icon-1 position-absolute ms-6">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor" />
                                            <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor" />
                                        </svg>
                                    </span>
                                    <input
                                        type="text"
                                        data-kt-user-table-filter="search"
                                        className="form-control form-control-solid w-250px ps-14"
                                        placeholder="Search user"
                                        value={apiData.search}
                                        onChange={(e) => setApiData({ ...apiData, search: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="card-body py-4">
                            {isLoader ? (
                                <Loader />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table align-middle table-row-dashed fs-6 gy-5">
                                        <thead>
                                            <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                <th className="min-w-125px">User</th>
                                                <th className="min-w-125px">Role</th>
                                                <th className="min-w-125px">Phone</th>
                                                <th className="min-w-125px">Status</th>
                                                <th className="min-w-125px">Joined Date</th>
                                                <th className="text-end min-w-100px">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-600 fw-semibold">
                                            {users.map((user, index) => (
                                                <tr key={index}>
                                                    <td className="d-flex align-items-center">
                                                        <div className="symbol symbol-circle symbol-50px overflow-hidden me-3">
                                                            <div className="symbol-label">
                                                                <img src={user.picture || "/assets/images/blank.png"} alt={user.name} className="w-100" />
                                                            </div>
                                                        </div>
                                                        <div className="d-flex flex-column">
                                                            <Link to={`/users/edit/${user.id}`} className="text-gray-800 text-hover-primary mb-1">
                                                                {user.name}
                                                            </Link>
                                                            <span className="text-gray-600 mb-1">{user.username}</span>
                                                            <span>{user.email}</span>
                                                        </div>
                                                    </td>
                                                    <td>{typeof user.role === 'object' ? user.role?.name : user.role}</td>
                                                    <td>{user.phone ? ` ${user.phone}` : '-'}</td>
                                                    <td>
                                                        {(() => {
                                                            const { badgeClass, label } = getGenericStatus(user.status);
                                                            return (
                                                                <span className={`badge ${badgeClass}`}>
                                                                    {label}
                                                                </span>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td>{formatDate(user.created_at)}</td>
                                                    <td className="text-end">
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Change Password</Tooltip>}>
                                                            <button onClick={() => openPasswordModal(user)} className="btn btn-icon btn-light btn-active-light-primary btn-sm me-2">
                                                                <i className="fas fa-key"></i>
                                                            </button>
                                                        </OverlayTrigger>
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Edit User</Tooltip>}>
                                                            <Link to={`/users/edit/${user.id}?role=${role}`} className="btn btn-icon btn-light btn-active-light-primary btn-sm me-2">
                                                                <i className="fas fa-edit"></i>
                                                            </Link>
                                                        </OverlayTrigger>
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Delete User</Tooltip>}>
                                                            <button onClick={() => handleDelete(user.id)} className="btn btn-icon btn-light btn-active-light-danger btn-sm">
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </OverlayTrigger>
                                                    </td>
                                                </tr>
                                            ))}
                                            {users.length === 0 && (
                                                <tr>
                                                    <td colSpan="5" className="text-center">No users found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <Paginator pagination={pagination} onPageChange={(page) => setApiData({ ...apiData, page })} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Change Password Modal */}
            <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Change Password</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>New Password</Form.Label>
                            <Form.Control 
                                type="password" 
                                placeholder="Enter new password"
                                value={passwordData.password}
                                onChange={(e) => setPasswordData({...passwordData, password: e.target.value})}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Confirm Password</Form.Label>
                            <Form.Control 
                                type="password" 
                                placeholder="Confirm new password"
                                value={passwordData.password_confirmation}
                                onChange={(e) => setPasswordData({...passwordData, password_confirmation: e.target.value})}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={submitPasswordChange} disabled={isLoader}>
                        {isLoader ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default List;
